// 🔧 content_resolver.js
// ✅ Purpose: Canonical Content Engine resolver
// ✅ Features:
// - Single gateway for all content retrieval
// - Cache-first architecture
// - Loads one content bundle per page/area
// - Prepares for future locale/profile/organization overrides
// - Falls back to code defaults when bundles are unavailable
// - Prevents React pages from directly reading Firestore
// - Central location for future bundle version validation

import {
  DEFAULT_SCOPE_ID,
} from "../constants/content_scopes";

import {
  DEFAULT_CONTENT_LOCALE,
} from "../constants/content_locales";

import {
  DEFAULT_CONTENT_PROFILE,
} from "../constants/content_profiles";

import {
  buildContentBundleKey,
} from "./content_bundle_key_helpers";

import {
  hasCachedBundle,
  getBundleContent,
  setCachedBundle,
} from "../cache/content_cache";

import {
  getPublishedBundle,
} from "../providers/firestore_read_provider";

/**
 * ============================================================================
 * CONTENT RESOLVER PHILOSOPHY
 * ============================================================================
 *
 * React pages should NEVER access Firestore directly.
 *
 * Pages request content.
 *
 * The resolver decides:
 *
 * Cache
 * ↓
 * Firestore Bundle
 * ↓
 * Code Fallback
 *
 * Future:
 *
 * Organization
 * Locale
 * Profile
 * Manifest Validation
 * Bundle Versions
 *
 * ============================================================================
 */

/**
 * Placeholder Firestore bundle loader.
 *
 * Phase 1 intentionally leaves Firestore implementation
 * outside of the resolver architecture.
 *
 * Later this function will read:
 *
 * /content_bundles/{bundleKey}
 */
async function loadBundleFromFirestore(bundleKey) {
  console.log(
    "[Communication Engine] Loading bundle:",
    bundleKey
  );

  return await getPublishedBundle(bundleKey);
}


/**
 * Placeholder code fallback.
 *
 * Future:
 *
 * This may read generated defaults,
 * embedded defaults,
 * or migration defaults.
 */
function getCodeFallback(area) {
  console.warn(
    "[Content Engine] Using code fallback:",
    area
  );

  return {};
}

/**
 * Resolves one Content Bundle.
 *
 * This is the ONLY public entry point
 * for page content retrieval.
 */
export async function resolveContent({
  area,

  scopeId = DEFAULT_SCOPE_ID,

  locale = DEFAULT_CONTENT_LOCALE,

  profile = DEFAULT_CONTENT_PROFILE,
}) {
  const bundleKey = buildContentBundleKey({
    scopeId,
    locale,
    profile,
    area,
  });

  /**
   * ----------------------------------------------------------
   * Memory Cache
   * ----------------------------------------------------------
   */

  if (hasCachedBundle(bundleKey)) {
    console.log(
      "[Content Engine] Bundle served from cache:",
      bundleKey
    );

    return getBundleContent(bundleKey);
  }

  /**
   * ----------------------------------------------------------
   * Firestore
   * ----------------------------------------------------------
   */

  const firestoreBundle =
    await loadBundleFromFirestore(bundleKey);

  if (firestoreBundle) {
    setCachedBundle(
      bundleKey,
      firestoreBundle
    );

    return firestoreBundle;
  }

  /**
   * ----------------------------------------------------------
   * Code Fallback
   * ----------------------------------------------------------
   */

  const fallback =
    getCodeFallback(area);

  setCachedBundle(
    bundleKey,
    fallback
  );

  return fallback;
}

/**
 * Convenience helper.
 *
 * Future admin tools may wish to
 * warm bundles before navigation.
 */
export async function preloadContent(options) {
  return resolveContent(options);
}

/**
 * Convenience helper.
 *
 * Future pages may simply call:
 *
 * await ensureContent(...)
 *
 * even if they don't immediately
 * consume the result.
 */
export async function ensureContent(options) {
  return resolveContent(options);
}